import { Injectable } from '@angular/core';

import { Action } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable, of, forkJoin, zip } from 'rxjs';
import { switchMap, catchError, filter, mergeMap } from 'rxjs/operators';

import {
  GetConclusions,
  RiskAssessmentActionTypes,
  GetOcrConclusionSuccess,
  GetLatestOcrConclusions,
  GetLatestOcrConclusionSuccess,
  GetFaceConclusionSuccess,
  GetLatestFaceConclusions,
  GetLatestFaceConclusionSuccess,
  GetOcrResults,
  GetOcrResultsSuccess,
  UpdateVerificationConclusion,
  UpdateVerificationConclusionFailure,
  GetOcrResultsFailure,
  GetConclusionsFailure,
  GetLatestOcrConclusionFailure,
  GetLatestFaceConclusionFailure,
  GetFaceResults,
  GetFaceResultsSuccess,
  GetFaceResultsFailure,
  GetReports,
  GetReportsSuccess,
  GetReportsFailure,
  CreateApprovalStatusSuccess,
  CreateApprovalStatus,
  CreateApprovalStatusFailure,
  CreateScreeningConclusion,
  CreateScreeningConclusionSuccess,
  CreateScreeningConclusionFailure,
  UpdateVerificationConclusionSuccess,
  GetApprovalStatus,
  GetApprovalStatusSuccess,
  GetApprovalStatusFailure,
  CreateRiskReport,
  CreateRiskReportSuccess,
  CreateRiskReportFailure,
} from 'store/actions/risk-assessment.actions';
import { UpdateCrpScreening, GetCustomer, GetCustomerStatus } from 'store/actions/customer.actions';

import { VerificationType, LatestVerificationConclusion, VerificationConclusion } from 'modules/shared/models/record.models';
import {
  VerificationConclusionsResponse, RecordOcrsResponse, VerificationConclusionsPayload,
  RecordsPayload, UpdateVerificationConclusionPayload, RecordFaceComparesResponse,
} from 'store/models/record.models';
import { Crp } from 'modules/shared/models/crp.models';
import { ScreeningConclusionPayload, ScreeningConclusion } from 'modules/shared/models/screening.models';
import { ApprovalStatus, RiskReport } from 'modules/shared/models/risk-assessment.models';
import {
  RiskReportPayload, RiskReportResponse, CreateApprovalStatusPayload,
  ApprovalStatusResponse, ApprovalStatusPayload
} from 'store/models/risk-assessment.models';
import { Customer } from 'modules/shared/models/customer.models';

import { RiskAssessmentService } from 'modules/core/services/risk-assessment.service';
import { CustomerService } from 'modules/core/services/customer.service';
import { PusherService } from '../../services/pusher.service';
import { SnackbarService } from 'app/services/snackbar.service';

@Injectable()
export class RiskAssessmentEffects {
  constructor(
    private actions$: Actions,
    private riskAssessmentService: RiskAssessmentService,
    private customerService: CustomerService,
    private snackBar: SnackbarService,
    private pusherService: PusherService
  ) { }

  @Effect() GetReports: Observable<Action> = this.actions$.pipe(
    ofType<GetReports>(RiskAssessmentActionTypes.GET_REPORTS),
    filter((input: any) => input.payload.customerId),
    mergeMap((res: any) => {
      return this.riskAssessmentService.getRiskReports(<RiskReportPayload>(
        res.payload
      )).pipe(
        switchMap((i: RiskReportResponse) => {
          return [new GetReportsSuccess(i), new GetApprovalStatus(<ApprovalStatusPayload>{
            customerId: res.payload.customerId,
            limit: 5
          })];
        }),
        catchError((err: any) => {
          this.snackBar.error('Error: Failed to retrieve risk reports');
          return of(new GetReportsFailure(err));
        })
      );
    }),
  );

  @Effect() GetOcrResults: Observable<Action> = this.actions$.pipe(
    ofType<GetOcrResults>(RiskAssessmentActionTypes.GET_OCR_RESULTS),
    mergeMap((input: any) => {
      return this.riskAssessmentService.getOcrResults(<RecordsPayload>(
        input.payload
      )).pipe(
        switchMap((res: RecordOcrsResponse) => {
          return of(new GetOcrResultsSuccess(res));
        }),
        catchError((err: any) => of(new GetOcrResultsFailure(err)))
      );
    }),
  );


  @Effect() CreateRiskReport: Observable<Action> = this.actions$.pipe(
    ofType<CreateRiskReport>(RiskAssessmentActionTypes.CREATE_RISK_REPORT),
    mergeMap((input: CreateRiskReport) => {
      return this.customerService.createRiskReports(input.payload.customerId).pipe(
        switchMap((riskReport: RiskReport) => {
          return zip(of(riskReport), this.customerService.getCustomer(input.payload.customerId), of(input.payload.redirectToScreening));
        }),
        switchMap(([riskReport, customer, redirect]: [RiskReport, Customer, boolean]) => {
          if (riskReport) {
            // if (riskReport.latestApprovalStatus && riskReport.latestApprovalStatus.approvalStatus === ApprovalStatusChoices.ACCEPTED) {
            //   this.snackBar.success(`Risk Assessment done. customer ${customer.name} has been automatically cleared by the system`);
            // } else {
            //   this.snackBar.success(`Risk Assessment done for customer ${customer.name}.
            //    Please submit screening conclusion(s) for all related parties to view risk report`);
            // }

            // temporarily not able to tell whether the customer is auto cleared, need BE changes
            this.snackBar.success(`Risk Assessment done for customer ${customer.name}.
              Please submit screening conclusion(s) for all related parties if the customer is not auto cleared.`);
          }
          return [new CreateRiskReportSuccess(riskReport), new GetCustomerStatus({
            customerId: input.payload.customerId
          })];
        }),
        catchError((err: any) => {
          this.snackBar.error('Error: Failed to generate risk report');
          return of(new CreateRiskReportFailure(err));
        })
      );
    })
  );

  @Effect() GetFaceResults: Observable<Action> = this.actions$.pipe(
    ofType<GetFaceResults>(RiskAssessmentActionTypes.GET_FACE_RESULTS),
    mergeMap((input: any) => {
      return this.riskAssessmentService.getFaceResults(<RecordsPayload>(
        input.payload
      )).pipe(
        switchMap((res: RecordFaceComparesResponse) => {
          return of(new GetFaceResultsSuccess(res));
        }),
        catchError((err: any) => of(new GetFaceResultsFailure(err)))
      );
    }),
  );

  @Effect() GetConclusions: Observable<Action> = this.actions$.pipe(
    ofType<GetConclusions>(RiskAssessmentActionTypes.GET_CONCLUSIONS),
    mergeMap((input: any) => {
      return this.riskAssessmentService
        .getVerificationConclusions(<VerificationConclusionsPayload>input.payload)
        .pipe(
          switchMap((conclusion: VerificationConclusionsResponse) => {
            return of([input.payload.verificationType, conclusion]);
          }),
          switchMap((res: any) => {
            const type: VerificationType = res[0];
            const conclusion: VerificationConclusionsResponse = res[1];
            if (type === VerificationType.OCR) {
              return of(new GetOcrConclusionSuccess(conclusion));
            } else {
              return of(new GetFaceConclusionSuccess(conclusion));
            }
          }),
          catchError((err: any) => of(new GetConclusionsFailure(err)))
        );
    }),
  );

  @Effect() GetLatestOcrConclusions: Observable<Action> = this.actions$.pipe(
    ofType<GetLatestOcrConclusions>(
      RiskAssessmentActionTypes.GET_LATEST_OCR_CONCLUSIONS
    ),
    mergeMap((input: any) => {
      const crps = input.payload ? input.payload : [];
      const obs: Observable<VerificationConclusionsResponse>[] = [];
      crps.forEach((crp: Crp) => {
        obs.push(
          this.riskAssessmentService.getVerificationConclusions(
            <VerificationConclusionsPayload>{
              crpId: crp.id,
              limit: 1,
              verificationType: VerificationType.OCR
            })
        );
      });
      return forkJoin(obs).pipe(
        switchMap((res: VerificationConclusionsResponse[]) => {
          const conclusions = res ? res : [];
          const latestConclusions: LatestVerificationConclusion = {};
          conclusions.forEach((conclusion: VerificationConclusionsResponse) => {
            if (conclusion.results[0]) {
              latestConclusions[conclusion.results[0].crp] = conclusion.results[0];
            }
          });
          return of(new GetLatestOcrConclusionSuccess(latestConclusions));
        }),
        catchError((err: any) => of(new GetLatestOcrConclusionFailure(err))),
      );
    })
  );

  @Effect() GetLatestFaceConclusions: Observable<Action> = this.actions$.pipe(
    ofType<GetLatestFaceConclusions>(
      RiskAssessmentActionTypes.GET_LATEST_FACE_CONCLUSIONS
    ),
    mergeMap((input: any) => {
      const crps = input.payload ? input.payload : [];
      const obs: Observable<VerificationConclusionsResponse>[] = [];
      crps.forEach((crp: Crp) => {
        obs.push(
          this.riskAssessmentService.getVerificationConclusions(
            <VerificationConclusionsPayload>{
              crpId: crp.id,
              limit: 1,
              verificationType: VerificationType.FACE
            })
        );
      });
      return forkJoin(obs).pipe(
        switchMap((res: VerificationConclusionsResponse[]) => {
          const conclusions = res ? res : [];
          const latestConclusions: LatestVerificationConclusion = {};
          conclusions.forEach((conclusion: VerificationConclusionsResponse) => {
            if (conclusion.results[0]) {
              latestConclusions[conclusion.results[0].crp] = conclusion.results[0];
            }
          });
          return of(new GetLatestFaceConclusionSuccess(latestConclusions));
        }),
        catchError((err: any) => of(new GetLatestFaceConclusionFailure(err)))
      );
    }),
  );

  @Effect() CreateScreeningConclusion: Observable<Action> = this.actions$.pipe(
    ofType<CreateScreeningConclusion>(
      RiskAssessmentActionTypes.CREATE_SCREENING_CONCLUSION
    ),
    filter((input: any) => input.payload.customer),
    mergeMap((input: any) => {
      return this.riskAssessmentService
        .createScreeningConclusion(<ScreeningConclusionPayload>input.payload)
        .pipe(
          switchMap((conclution: ScreeningConclusion) => {
            return [
              new CreateScreeningConclusionSuccess(conclution),
              new UpdateCrpScreening(conclution),
            ];
          }),
          catchError((err: any) => {
            this.snackBar.error('Error: Failed to update screening conclusion');
            return of(new CreateScreeningConclusionFailure(err));
          })
        );
    }),
  );

  @Effect() UpdateVerificationConclusion: Observable<Action> = this.actions$.pipe(
    ofType<UpdateVerificationConclusion>(
      RiskAssessmentActionTypes.UPDATE_VERIFICATION_CONCLUSIONS
    ),
    switchMap((input: any) => {
      return this.riskAssessmentService
        .updateVerificationConclusions(<UpdateVerificationConclusionPayload>(
          input.payload
        )).pipe(
          switchMap((conclution: VerificationConclusion) => [
            new GetConclusions(
              <VerificationConclusionsPayload>{
                crpId: input.payload.crpId,
                limit: input.payload.limit,
                offset: input.payload.offset,
                sort: input.payload.sort,
                verificationType: input.payload.verificationType
              }
            ),
            new UpdateVerificationConclusionSuccess(conclution),
          ]),
          catchError((err: any) => {
            this.snackBar.error('Error: Failed to update verification conclusion');
            return of(new UpdateVerificationConclusionFailure(err));
          })
        );
    }),
  );

  @Effect() GetApprovalStatus: Observable<Action> = this.actions$.pipe(
    ofType<GetApprovalStatus>(
      RiskAssessmentActionTypes.GET_APPROVAL_STATUS
    ),
    filter((input: any) => input.payload.customerId),
    mergeMap((input: any) => {
      return this.riskAssessmentService.getApprovalStatuses(
        <ApprovalStatusPayload>input.payload
      ).pipe(
        switchMap((res: ApprovalStatusResponse) => {
          return of(new GetApprovalStatusSuccess(res));
        }),
        catchError((err: any) => of(new GetApprovalStatusFailure(err)))
      );
    }),
  );

  @Effect() CreateApprovalStatus: Observable<Action> = this.actions$.pipe(
    ofType<CreateApprovalStatus>(
      RiskAssessmentActionTypes.CREATE_APPROVAL_STATUS
    ),
    filter((input: any) => input.payload.customerId),
    switchMap((input: any) => {
      return this.riskAssessmentService.createApprovalStatus(
        <CreateApprovalStatusPayload>input.payload
      ).pipe(
        switchMap((status: ApprovalStatus) => {
          this.snackBar.success('Approval status successfully updated');
          return [
            new CreateApprovalStatusSuccess(status),
            new GetApprovalStatus(<ApprovalStatusPayload>{
              customerId: input.payload.customerId,
              limit: 5
            }),
            new GetCustomer({ customerId: input.payload.customerId }),
          ];
        }),
        catchError((err: any) => {
          if (err.status === 403) {
            this.snackBar.error(err.error.detail);
          } else {
            this.snackBar.error('Error: Failed to update approval status');
          }

          return of(new CreateApprovalStatusFailure(err));
        })
      );
    }),
  );

  @Effect({ dispatch: false }) CreateRiskReportSuccess$: Observable<object> = this.actions$.pipe(
    ofType<CreateRiskReportSuccess>(RiskAssessmentActionTypes.CREATE_RISK_REPORT_SUCCESS),
    switchMap((riskReportResponse: CreateRiskReportSuccess) => {
      this.pusherService.requestReloadScreening({
        customerId: riskReportResponse.payload.customer
      });
      return of({});
    })
  );
}
